import { useCallback, useEffect, useRef, useState } from 'react';
import { buildSessionRecap, completeDebriefRun, fetchCoachingGuidance } from '../services/api';
import {
  composeDetailedAnalysis,
  validateDetailedChatAnswer,
  validateDetailedNarrative,
} from '../utils/detailedSessionAnalysis';
import {
  DEEP_CHAT_MIN_CONTEXT,
  DEEP_CHAT_NUM_PREDICT,
  fitDeepChatMessages,
  formatGuidance,
  referencedPriorityCorners,
  suggestedDeepQuestions,
} from '../utils/deepAnalysisChat';
import { fetchOllamaModels, streamOllamaChat, streamOllamaMessages } from '../utils/llmStream';
import { pickRecapModel } from '../utils/sessionRecap';

const activeStatus = status => ['building', 'streaming', 'correcting'].includes(status);
const initialDeepChat = () => ({
  messages: [], status: 'idle', error: null, trimmedPairs: 0,
});

function briefingMeta(briefing, kind) {
  const debrief = briefing.debrief || {};
  const capabilities = briefing.capabilities || {};
  return {
    headline: briefing.headline || null,
    nDays: debrief.n_days ?? 0,
    nSessions: debrief.n_sessions ?? 0,
    hasHistory: !!debrief.has_history,
    whereTimeMarkdown: debrief.where_time_markdown || null,
    nextSessionFocusMarkdown: debrief.next_session_focus_markdown || null,
    carHealthMarkdown: debrief.car_health_markdown || null,
    gaps: debrief.gaps || [],
    sectorGaps: debrief.sector_gaps || [],
    primaryGapBasis: debrief.primary_gap_basis || null,
    analysisKind: briefing.analysis_kind || kind,
    evidenceFingerprint: briefing.evidence_fingerprint || null,
    confidence: briefing.confidence || null,
    cleanLapCount: briefing.clean_lap_count ?? 0,
    priorityCorners: briefing.priority_corners || [],
    allowedNumericFacts: briefing.allowed_numeric_facts || [],
    detailedVerifiedSections: briefing.detailed_verified_sections || null,
    runId: briefing.run_id || null,
    availableChannels: capabilities.available_labels || [],
    unavailableChannels: capabilities.absent_labels || [],
  };
}

export default function useSessionRecap({
  sessionId,
  corners,
  enabled = true,
  allowPromptDebug = false,
  traceCoaching = false,
  // Server-served model + context (GET /api/config). Empty/0 fields fall back
  // to the built-in defaults, so an unreachable backend still works.
  coachModel = {},
}) {
  const [recap, setRecap] = useState(null);
  const [deepAnalysis, setDeepAnalysis] = useState(null);
  const [deepChat, setDeepChat] = useState(initialDeepChat);
  const [queuedKind, setQueuedKind] = useState(null);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);
  const [model, setModelState] = useState(() => {
    try { return localStorage.getItem('trackapp:recap_model') || ''; }
    catch { return ''; }
  });
  // One resolved context for every call in the flow — see DEEP_CHAT_MIN_CONTEXT
  // for why they must all match. The served value wins; the constant is the
  // fallback for when /config never answered.
  const coachNumCtx = coachModel.numCtx || DEEP_CHAT_MIN_CONTEXT;
  const abortRef = useRef(null);
  const chatAbortRef = useRef(null);
  const chatRequestIdRef = useRef(0);
  const chatBusyRef = useRef(false);
  const requestIdRef = useRef(0);
  const runningKindRef = useRef(null);
  const cornerSignatureRef = useRef(null);
  const cornerSignature = JSON.stringify((corners || []).map(corner => [
    corner.id, corner.label, corner.apex_lat, corner.apex_lon,
  ]));

  const setReport = useCallback((kind, value) => {
    const setter = kind === 'detailed' ? setDeepAnalysis : setRecap;
    setter(value);
  }, []);

  const resetDeepChat = useCallback(() => {
    chatRequestIdRef.current += 1;
    chatBusyRef.current = false;
    chatAbortRef.current?.abort();
    chatAbortRef.current = null;
    setDeepChat(initialDeepChat());
  }, []);

  const runGeneration = useCallback(async kind => {
    if (!enabled || !sessionId || runningKindRef.current || chatBusyRef.current) return;
    if (kind === 'detailed') resetDeepChat();
    runningKindRef.current = kind;
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setReport(kind, { status: 'building', text: '', meta: null, error: null });
    let detailedFallbackState = null;
    const reflection = reflectionRef.current;
    // Provenance: what the model actually produced, reported back once the
    // stream finishes. Only the browser knows this -- it is what streams.
    const startedAt = Date.now();
    let runId = null;

    try {
      const [briefing, availableModels] = await Promise.all([
        buildSessionRecap(sessionId, corners, kind, reflection),
        models.length
          ? Promise.resolve(models)
          : fetchOllamaModels().catch(error => (
            kind === 'detailed' ? [] : Promise.reject(error)
          )),
      ]);
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;
      if (!briefing?.system_prompt || !briefing?.user_prompt) {
        throw new Error(`${kind === 'detailed' ? 'deep analysis' : 'recap'} briefing unavailable`);
      }
      if (!models.length) setModels(availableModels);

      const selectedAdminModel = allowPromptDebug
        && availableModels.some(item => item.name === model) ? model : null;
      const selectedModel = selectedAdminModel || pickRecapModel(availableModels, coachModel);
      if (allowPromptDebug && selectedModel && model !== selectedModel) {
        setModelState(selectedModel);
      }

      const meta = briefingMeta(briefing, kind);
      runId = meta.runId;
      const promptDebug = {
        model: selectedModel,
        system: briefing.system_prompt,
        user: briefing.user_prompt,
        headline: briefing.headline || '',
        whereTime: briefing.debrief?.where_time_markdown || '',
        nextSessionFocus: briefing.debrief?.next_session_focus_markdown || '',
      };
      const chatContext = kind === 'detailed' ? {
        system: briefing.chat_system_prompt || '',
        evidence: briefing.chat_evidence || '',
      } : null;
      if (kind === 'detailed') {
        detailedFallbackState = {
          text: composeDetailedAnalysis('', meta.detailedVerifiedSections),
          meta, promptDebug, chatContext,
        };
      }
      if (!selectedModel) {
        if (kind !== 'detailed') throw new Error('no Ollama model installed');
        setReport(kind, {
          status: 'done',
          ...detailedFallbackState,
          chatReady: false,
          validationWarning: null,
          error: null,
        });
        return;
      }

      const maxAttempts = kind === 'detailed' ? 2 : 1;
      let validationErrors = [];
      let accumulated = '';
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        accumulated = '';
        const correcting = attempt > 0;
        const correctionSummary = validationErrors.map(error => (
          error.startsWith('Unsupported numbers:')
            ? 'The prior response introduced unsupported numbers.'
            : error
        )).join(' ');
        const userPrompt = correcting
          ? `${briefing.user_prompt}\n\nCORRECTION REQUIRED: ${correctionSummary} Rewrite only the two narrative sections from the original evidence. Do not mention this correction.`
          : briefing.user_prompt;
        setReport(kind, {
          status: correcting ? 'correcting' : 'streaming',
          text: kind === 'detailed'
            ? composeDetailedAnalysis('', meta.detailedVerifiedSections)
            : '',
          meta, promptDebug, chatContext, chatReady: false, error: null,
        });
        await streamOllamaChat({
          model: selectedModel,
          system: briefing.system_prompt,
          user: userPrompt,
          numPredict: kind === 'detailed' ? 700 : 900,
          // Same num_ctx as the follow-up chat, deliberately — see the note on
          // DEEP_CHAT_MIN_CONTEXT. Sizing each call to its own prompt is what
          // made Ollama reload the model on every step of the flow.
          minContext: coachNumCtx,
          signal: controller.signal,
          onToken: token => {
            if (requestId !== requestIdRef.current) return;
            accumulated += token;
            setReport(kind, {
              status: correcting ? 'correcting' : 'streaming',
              text: kind === 'detailed'
                ? composeDetailedAnalysis(accumulated, meta.detailedVerifiedSections)
                : accumulated,
              meta, promptDebug, chatContext, chatReady: false, error: null,
            });
          },
        });
        if (controller.signal.aborted || requestId !== requestIdRef.current) return;
        validationErrors = kind === 'detailed'
          ? validateDetailedNarrative(accumulated, meta) : [];
        if (!validationErrors.length) break;
      }

      if (!controller.signal.aborted && requestId === requestIdRef.current) {
        if (allowPromptDebug && validationErrors.length) {
          promptDebug.validationErrors = validationErrors;
        }
        completeDebriefRun(runId, {
          narrative: validationErrors.length ? null : accumulated,
          validation: validationErrors.length ? { errors: validationErrors } : { ok: true },
          fallback_reason: validationErrors.length
            ? 'narrative failed validation; verified sections only' : null,
          backend: 'ollama',
          model: selectedModel,
          latency_ms: Date.now() - startedAt,
        });
        setReport(kind, {
          status: 'done',
          text: kind === 'detailed'
            ? composeDetailedAnalysis(
              validationErrors.length ? '' : accumulated,
              meta.detailedVerifiedSections,
            )
            : accumulated,
          meta, promptDebug, chatContext,
          chatReady: kind === 'detailed' && !!chatContext?.system && !!chatContext?.evidence,
          validationWarning: null,
          error: null,
        });
      }
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted
          || requestId !== requestIdRef.current) return;
      console.error(`AI session ${kind} failed:`, error);
      if (kind === 'detailed' && detailedFallbackState) {
        completeDebriefRun(runId, {
          narrative: null,
          fallback_reason: error.message || 'generation failed',
          latency_ms: Date.now() - startedAt,
        });
        setReport(kind, {
          status: 'done',
          ...detailedFallbackState,
          chatReady: false,
          validationWarning: null,
          error: null,
        });
      } else {
        setReport(kind, { status: 'error', text: '', meta: null, error: error.message });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        runningKindRef.current = null;
        abortRef.current = null;
      }
    }
  }, [allowPromptDebug, coachModel, coachNumCtx, corners, enabled, model, models,
      resetDeepChat, sessionId, setReport]);

  // What the driver said about the session, if they chose to say anything.
  // Optional by design: a required form before analysis would be a toll gate
  // on the feature's main path.
  const reflectionRef = useRef(null);
  const setReflection = useCallback(value => { reflectionRef.current = value || null; }, []);

  const requestGeneration = useCallback(kind => {
    if (!enabled || !sessionId || chatBusyRef.current) return;
    if (runningKindRef.current) {
      if (runningKindRef.current === kind) return;
      setQueuedKind(kind);
      setReport(kind, current => ({
        ...(current || {}),
        status: 'queued',
        text: current?.text || '',
        meta: current?.meta || null,
        error: null,
      }));
      return;
    }
    runGeneration(kind);
  }, [enabled, runGeneration, sessionId, setReport]);

  const generate = useCallback(() => requestGeneration('standard'), [requestGeneration]);
  const generateDeep = useCallback(() => requestGeneration('detailed'), [requestGeneration]);

  const stopKind = useCallback(kind => {
    if (queuedKind === kind) {
      setQueuedKind(null);
      setReport(kind, current => current?.text
        ? { ...current, status: 'done' } : null);
      return;
    }
    if (runningKindRef.current !== kind) return;
    requestIdRef.current += 1;
    runningKindRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
    setReport(kind, current => current?.text
      ? { ...current, status: 'done' } : null);
  }, [queuedKind, setReport]);

  const dismissKind = useCallback(kind => {
    if (runningKindRef.current === kind) {
      requestIdRef.current += 1;
      runningKindRef.current = null;
      abortRef.current?.abort();
      abortRef.current = null;
    }
    if (queuedKind === kind) setQueuedKind(null);
    if (kind === 'detailed') resetDeepChat();
    setReport(kind, null);
  }, [queuedKind, resetDeepChat, setReport]);

  const setModel = useCallback(nextModel => {
    setModelState(nextModel);
    setRecap(null);
    setDeepAnalysis(null);
    resetDeepChat();
    try { localStorage.setItem('trackapp:recap_model', nextModel); }
    catch { /* localStorage unavailable */ }
  }, [resetDeepChat]);

  const askDeep = useCallback(async rawQuestion => {
    const question = String(rawQuestion || '').trim().slice(0, 1000);
    if (!question || !enabled || !sessionId || deepAnalysis?.status !== 'done'
        || !deepAnalysis?.chatReady
        || runningKindRef.current || chatBusyRef.current) return;
    const chatContext = deepAnalysis.chatContext || {};
    if (!chatContext.system || !chatContext.evidence) {
      setDeepChat(current => ({
        ...current, error: 'Follow-up context is unavailable. Regenerate Deep Analysis.',
      }));
      return;
    }

    const selectedModel = deepAnalysis.promptDebug?.model || model;
    if (!selectedModel) {
      setDeepChat(current => ({ ...current, error: 'No Ollama model is available.' }));
      return;
    }

    const requestId = ++chatRequestIdRef.current;
    const controller = new AbortController();
    const userId = `deep-user-${requestId}`;
    const assistantId = `deep-assistant-${requestId}`;
    chatBusyRef.current = true;
    chatAbortRef.current = controller;

    const updateAssistant = patch => setDeepChat(current => ({
      ...current,
      messages: current.messages.map(message => (
        message.id === assistantId ? { ...message, ...patch } : message
      )),
    }));

    // Show the question and a streaming placeholder BEFORE any await. chatBusyRef
    // is a ref, so setting it re-renders nothing: with a network round-trip for
    // guidance ahead of the first setState, the panel sat visibly dead for up to
    // four seconds and further clicks were swallowed by the busy guard. The user
    // could only conclude the button was broken.
    setDeepChat(current => ({
      ...current,
      messages: [
        ...current.messages,
        { id: userId, role: 'user', text: question, status: 'done' },
        { id: assistantId, role: 'assistant', text: '', status: 'streaming', cornerRefs: [] },
      ],
      status: 'streaming',
      error: null,
    }));

    // Retrieved background, if any is available. Awaited before fitting rather
    // than streamed in later: it has to be inside the context budget or not
    // present at all.
    const guidance = traceCoaching
      ? formatGuidance(await fetchCoachingGuidance(question))
      : '';

    let fitted;
    try {
      fitted = fitDeepChatMessages({
        system: chatContext.system,
        evidence: chatContext.evidence,
        headline: deepAnalysis.meta?.headline,
        report: deepAnalysis.text,
        turns: deepChat.messages,
        question,
        guidance,
        // Must match the num_ctx actually requested below: the fitter's token
        // budget is derived from it, and packing past the real context makes
        // llama.cpp evict from the START — the system prompt — which is what
        // degenerates the model into echoing the briefing.
        maxContext: coachNumCtx,
      });
    } catch (error) {
      chatBusyRef.current = false;
      chatAbortRef.current = null;
      updateAssistant({ text: '', status: 'error', error: error.message });
      setDeepChat(current => ({ ...current, status: 'idle', error: error.message }));
      return;
    }

    setDeepChat(current => ({ ...current, trimmedPairs: fitted.trimmedPairs }));

    try {
      let validationErrors = [];
      let accumulated = '';
      for (let attempt = 0; attempt < 2; attempt += 1) {
        accumulated = '';
        const messages = attempt === 0 ? fitted.messages : [
          ...fitted.messages,
          {
            role: 'user',
            content: `CORRECTION REQUIRED: ${validationErrors.join(' ')} Answer my preceding question again from the original evidence. Do not mention this correction.`,
          },
        ];
        if (attempt > 0) {
          setDeepChat(current => ({ ...current, status: 'checking' }));
          updateAssistant({ text: '', status: 'streaming' });
        }
        await streamOllamaMessages({
          model: selectedModel,
          messages,
          numPredict: DEEP_CHAT_NUM_PREDICT,
          minContext: coachNumCtx,
          maxContext: coachNumCtx,
          signal: controller.signal,
          onToken: token => {
            if (requestId !== chatRequestIdRef.current) return;
            accumulated += token;
            updateAssistant({ text: accumulated, status: 'streaming' });
          },
        });
        if (controller.signal.aborted || requestId !== chatRequestIdRef.current) return;
        validationErrors = validateDetailedChatAnswer(accumulated, deepAnalysis.meta);
        if (!validationErrors.length) break;
      }

      if (validationErrors.length) {
        updateAssistant({
          text: '', status: 'error',
          error: 'Mistral could not produce an answer that stayed within the measured evidence. Try rephrasing the question.',
        });
        setDeepChat(current => ({ ...current, status: 'idle' }));
      } else {
        updateAssistant({
          text: accumulated,
          status: 'done',
          cornerRefs: referencedPriorityCorners(
            accumulated, deepAnalysis.meta?.priorityCorners),
        });
        setDeepChat(current => ({ ...current, status: 'idle', error: null }));
      }
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted
          || requestId !== chatRequestIdRef.current) return;
      updateAssistant({ text: '', status: 'error', error: error.message });
      setDeepChat(current => ({ ...current, status: 'idle' }));
    } finally {
      if (requestId === chatRequestIdRef.current) {
        chatBusyRef.current = false;
        chatAbortRef.current = null;
      }
    }
  }, [coachNumCtx, deepAnalysis, deepChat.messages, enabled, model, sessionId, traceCoaching]);

  const stopDeepChat = useCallback(() => {
    if (!chatBusyRef.current) return;
    chatRequestIdRef.current += 1;
    chatBusyRef.current = false;
    chatAbortRef.current?.abort();
    chatAbortRef.current = null;
    setDeepChat(current => ({
      ...current,
      status: 'idle',
      messages: current.messages.map(message => (
        message.status === 'streaming'
          ? { ...message, status: 'stopped', text: message.text || 'Response stopped.' }
          : message
      )),
    }));
  }, []);

  const busy = activeStatus(recap?.status) || recap?.status === 'queued';
  const deepBusy = activeStatus(deepAnalysis?.status) || deepAnalysis?.status === 'queued';
  const chatBusy = ['streaming', 'checking'].includes(deepChat.status);

  useEffect(() => {
    if (!queuedKind || runningKindRef.current) return;
    const nextKind = queuedKind;
    setQueuedKind(null);
    runGeneration(nextKind);
  }, [deepAnalysis?.status, queuedKind, recap?.status, runGeneration]);

  useEffect(() => {
    requestIdRef.current += 1;
    runningKindRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
    setQueuedKind(null);
    setRecap(null);
    setDeepAnalysis(null);
    resetDeepChat();
  }, [enabled, resetDeepChat, sessionId]);

  useEffect(() => {
    if (cornerSignatureRef.current == null) {
      cornerSignatureRef.current = cornerSignature;
      return;
    }
    if (cornerSignatureRef.current === cornerSignature) return;
    cornerSignatureRef.current = cornerSignature;
    if (runningKindRef.current === 'detailed') {
      requestIdRef.current += 1;
      runningKindRef.current = null;
      abortRef.current?.abort();
      abortRef.current = null;
    }
    if (queuedKind === 'detailed') setQueuedKind(null);
    setDeepAnalysis(null);
    resetDeepChat();
  }, [cornerSignature, queuedKind, resetDeepChat]);

  useEffect(() => {
    if (!allowPromptDebug || !enabled) return undefined;
    let cancelled = false;
    setModelsLoading(true);
    setModelsError(null);
    fetchOllamaModels()
      .then(availableModels => {
        if (cancelled) return;
        setModels(availableModels);
        setModelState(current => {
          const selected = availableModels.some(item => item.name === current)
            ? current : (pickRecapModel(availableModels, coachModel) || '');
          try {
            if (selected) localStorage.setItem('trackapp:recap_model', selected);
          } catch { /* localStorage unavailable */ }
          return selected;
        });
      })
      .catch(error => {
        if (!cancelled) setModelsError(error.message);
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => { cancelled = true; };
  }, [allowPromptDebug, coachModel, enabled]);

  useEffect(() => () => {
    abortRef.current?.abort();
    chatAbortRef.current?.abort();
  }, []);

  return {
    recap,
    deepAnalysis,
    deepChat,
    busy,
    deepBusy,
    chatBusy,
    anyBusy: busy || deepBusy || chatBusy,
    runningKind: runningKindRef.current,
    queuedKind,
    models,
    modelsLoading,
    modelsError,
    model,
    setModel,
    generate,
    generateDeep,
    setReflection,
    traceCoaching,
    askDeep,
    stopDeepChat,
    clearDeepChat: resetDeepChat,
    deepQuestionSuggestions: suggestedDeepQuestions(deepAnalysis?.meta),
    stop: () => stopKind('standard'),
    stopDeep: () => stopKind('detailed'),
    dismiss: () => dismissKind('standard'),
    dismissDeep: () => dismissKind('detailed'),
  };
}
